import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Eye, Pencil, Copy, Trash2, MoreVertical } from "lucide-react";
import { Button } from "../../base/button/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "../../base/dropdown-menu/dropdown-menu";
import { useTranslation } from "react-i18next";
/**
 * Reusable action menu for table rows (View Detail/Edit/Delete)
 *
 * @example
 * ```tsx
 * {
 *   id: 'actions',
 *   cell: ({ row }) => (
 *     <EntityActions
 *       onView={() => handleView(row.original)}
 *       onEdit={() => handleEdit(row.original)}
 *       onDelete={() => handleDelete(row.original.id)}
 *       customActions={[
 *         {
 *           label: 'Suspend',
 *           icon: <Ban className="h-4 w-4" />,
 *           onClick: () => handleSuspend(row.original.id),
 *           variant: 'destructive'
 *         }
 *       ]}
 *     />
 *   )
 * }
 * ```
 */ export function EntityActions({ onView, onEdit, onClone, onDelete, editLabel, viewDisabled = false, editDisabled = false, cloneDisabled = false, deleteDisabled = false, customActions = [], pending = false }) {
    const { t } = useTranslation();
    return /*#__PURE__*/ _jsxs(DropdownMenu, {
        children: [
            /*#__PURE__*/ _jsx(DropdownMenuTrigger, {
                asChild: true,
                disabled: pending,
                children: /*#__PURE__*/ _jsxs(Button, {
                    variant: "ghost",
                    size: "icon",
                    className: "h-8 w-8",
                    loading: pending,
                    children: [
                        /*#__PURE__*/ _jsx(MoreVertical, {
                            className: "h-4 w-4"
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            className: "sr-only",
                            children: t("common.actions")
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs(DropdownMenuContent, {
                align: "end",
                children: [
                    onView && /*#__PURE__*/ _jsxs(DropdownMenuItem, {
                        onClick: onView,
                        disabled: viewDisabled,
                        children: [
                            /*#__PURE__*/ _jsx(Eye, {
                                className: "h-4 w-4"
                            }),
                            t("common.viewDetail")
                        ]
                    }),
                    onEdit && /*#__PURE__*/ _jsxs(DropdownMenuItem, {
                        onClick: onEdit,
                        disabled: editDisabled,
                        children: [
                            /*#__PURE__*/ _jsx(Pencil, {
                                className: "h-4 w-4"
                            }),
                            editLabel ?? t("common.edit")
                        ]
                    }),
                    onClone && /*#__PURE__*/ _jsxs(DropdownMenuItem, {
                        onClick: onClone,
                        disabled: cloneDisabled,
                        children: [
                            /*#__PURE__*/ _jsx(Copy, {
                                className: "h-4 w-4"
                            }),
                            t("common.clone")
                        ]
                    }),
                    customActions.length > 0 && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(DropdownMenuSeparator, {}),
                            customActions.map((action, index)=>/*#__PURE__*/ _jsxs(DropdownMenuItem, {
                                    onClick: action.onClick,
                                    disabled: action.disabled,
                                    className: action.variant === "destructive" ? "text-destructive focus:text-destructive" : "",
                                    children: [
                                        action.icon,
                                        action.label
                                    ]
                                }, index))
                        ]
                    }),
                    onDelete && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(DropdownMenuSeparator, {}),
                            /*#__PURE__*/ _jsxs(DropdownMenuItem, {
                                onClick: onDelete,
                                disabled: deleteDisabled,
                                className: "text-destructive focus:text-destructive",
                                children: [
                                    /*#__PURE__*/ _jsx(Trash2, {
                                        className: "h-4 w-4"
                                    }),
                                    t("common.delete")
                                ]
                            })
                        ]
                    })
                ]
            })
        ]
    });
}

//# sourceMappingURL=EntityActions.js.map